import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';

gsap.registerPlugin(MorphSVGPlugin, ScrollTrigger);


const ACCENT_COLORS = [
	'#F75454', // Red Accent
	'#C77BF7', // Purple Accent
	'#27BEF5', // Blue Accent
	'#2CE6C5', // Teal Accent
	'#74F35F', // Green Accent
	'#CAEF57', // Yellow Green Accent
	'#EEF315', // Yellow Accent
];

function pickRandomAccentColor(): string {
	return ACCENT_COLORS[Math.floor(Math.random() * ACCENT_COLORS.length)];
}

/**
 * Lenis スムーススクロールの初期化
 */
function initLenis(): Lenis {
	const lenis = new Lenis({
		duration: 1.2,
		easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
		orientation: 'vertical',
		gestureOrientation: 'vertical',
		smoothWheel: true,
	});

	function raf(time: number) {
		lenis.raf(time);
		requestAnimationFrame(raf);
	}

	requestAnimationFrame(raf);

	(window as unknown as { __lenis?: Lenis }).__lenis = lenis;

	return lenis;
}

function initHeroAnimation(): void {
	/**
	 * Hero Block - View Script
	 *
	 * フロントエンドで動画の再生制御などを行う場合はここに追加
	 */
	const mm = gsap.matchMedia();
	const heroBlock = document.querySelector<HTMLElement>(
		'.wp-block-best-anniversary-blocks-hero'
	);
	const philosophy = document.querySelector<HTMLElement>('.top-philosophy');
	if (!philosophy || !heroBlock) {
		return;
	}
	const mvMain = document.querySelector<HTMLElement>('.hero__background');
	const copyIntroOrder = [3, 2, 1];

	// コピーのイントロアニメーション
	if (copyIntroOrder.length) {
		const copyIntroTl = gsap.timeline({
			defaults: {
				duration: 0.35,
			},
		});

		const pathGroups = copyIntroOrder.map((itemNo) =>
			gsap.utils.toArray(`.hero__copy__item.--item0${itemNo} path`)
		);
		const maxPathLength = Math.max(
			...pathGroups.map((paths) => paths.length),
			0
		);

		for (let pathIndex = 0; pathIndex < maxPathLength; pathIndex += 1) {
			const stepTargets = pathGroups
				.map((paths) => paths[pathIndex])
				.filter(Boolean);

			if (!stepTargets.length) {
				continue;
			}

			copyIntroTl.fromTo(
				stepTargets,
				{ yPercent: 150, opacity: 1 },
				{ yPercent: 0, opacity: 1 },
				pathIndex === 0 ? 0 : '>-0.25'
			);
		}
	}

	// スクロールアニメーション（pin付き）
	if (heroBlock && mvMain) {
		const mvTl = gsap.timeline({
			scrollTrigger: {
			trigger: mvMain,
			start: 'top top',
			end: () => {
				const start = mvMain.getBoundingClientRect().top + window.scrollY;
				const max = ScrollTrigger.maxScroll(window);
				return `+=${Math.max(0, max - start)}`;
			},
			scrub: true,
			invalidateOnRefresh: true,
			},
		});
		
		mvTl.to(mvMain, {
			y: () => {
			const start = mvMain.getBoundingClientRect().top + window.scrollY;
			const max = ScrollTrigger.maxScroll(window);
			return Math.max(0, max - start);
			},
			ease: 'none',
		})
		.to(".hero__grad",{scaleY:20,
			duration:0.6,
		},0)
		

		// コピーのスケールアニメーション
		// mvMain は mvTl の y トランスフォームで視覚的に固定されるため、
		// 途中ロード時に getBoundingClientRect() が scrollY 分ズレる。
		// GSAP に触れない静的な heroBlock をトリガーに使う。
		const setupCopyScrollAnimation = (
			yBase: number,
			scaleStep: number
		): (() => void) => {
			const copyTl = gsap.timeline({
				scrollTrigger: {
					trigger: heroBlock,
					start: 'top top',
					end: 'bottom-=50% top',
					scrub: true,
					invalidateOnRefresh: true,
				},
			});

			[3, 2].forEach((itemNo, i) => {
				const n = i + 1;

				copyTl.to(
					`.hero__copy__item.--item0${itemNo}`,
					{
						scale: 1 - scaleStep * n,
						yPercent: yBase * n,
					},
					0
				);
			});

			return () => {
				copyTl.kill();
			};
		};

		mm.add('(min-width: 782px)', () => setupCopyScrollAnimation(70, 0.15));
		mm.add('(max-width: 781px)', () => setupCopyScrollAnimation(60, 0.15));
	}

}

/**
 * top-jobセクションのアニメーション初期化
 */
function initTopJobAnimation(): void {
	const topJob = document.querySelector<HTMLElement>('.top-job');
	if (!topJob) {
		return;
	}

	const head = topJob.querySelector<HTMLElement>('.top-job-head');
	const headContent = topJob.querySelector<HTMLElement>(
		'.top-job-head-content'
	);
	const contents = topJob.querySelector<HTMLElement>('.top-job-contents');
	const mainEn = topJob.querySelector<HTMLElement>(
		'.wp-block-best-anniversary-blocks-job-template__term-en'
	);

	const chars = mainEn ? mainEn.querySelectorAll<HTMLElement>('.char') : [];

	if (head) {
		gsap.set(head, { scaleX: 0.9, y: 20 });
	}
	if (headContent) {
		gsap.set(headContent, { scaleX: 1.1, autoAlpha: 0 });
	}
	if (contents) {
		gsap.set(contents, { scaleX: 0.9 });
	}
	if (chars.length) {
		gsap.set(chars, {
			clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
			y: '80%',
		});
	}

	const headTl = gsap.timeline({
		scrollTrigger: {
			trigger: topJob,
			start: 'top 90%',
			once: true,
		},
	});

	if (head) {
		headTl.to(
			head,
			{
				scaleX: 1,
				y: 0,
				duration: 0.4,
				ease: 'power2.out',
			},
			0
		);
	}

	if (headContent) {
		headTl.to(
			headContent,
			{
				scaleX: 1,
				autoAlpha: 1,
				duration: 0.4,
				ease: 'power2.out',
			},
			0
		);
	}

	if (contents) {
		const contentsTl = gsap.timeline({
			scrollTrigger: {
				trigger: contents,
				start: 'top 80%',
				once: true,
			},
		});

		contentsTl.to(
			contents,
			{
				scaleX: 1,
				duration: 0.4,
				ease: 'power2.out',
			},
			0
		);

		if (chars.length) {
			contentsTl.to(
				chars,
				{
					clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
					duration: 0.4,
					ease: 'power2.out',
					stagger: 0.03,
				},
				0.2
			);
			contentsTl.to(
				chars,
				{
					y: 0,
					duration: 0.6,
					ease: 'power2.out',
					stagger: 0.03,
				},
				0.3
			);
		}
	}
}

/**
 * 指定セレクタの各要素に対し、scaleX 0.9 → 1 と直下要素の autoAlpha 0 → 1 を適用
 * 同時にビューポートに入った要素は stagger で順番に発火する
 * （top-job の contents 部分と同等の動き。head 等の下層構造を持たないブロック用）
 * @param selector
 */
function initBlockScaleInAnimation(selector: string): void {
	const blocks = document.querySelectorAll<HTMLElement>(selector);
	if (!blocks.length) {
		return;
	}

	blocks.forEach((block) => {
		gsap.set(block, { scaleX: 0.9 });
		if (block.children.length) {
			gsap.set(block.children, { autoAlpha: 0 });
		}
	});

	ScrollTrigger.batch(blocks, {
		start: 'top 80%',
		once: true,
		onEnter: (batch) => {
			batch.forEach((el, i) => {
				const block = el as HTMLElement;
				const tl = gsap.timeline({ delay: i * 0.15 });
				tl.to(block, {
					scaleX: 1,
					duration: 0.4,
					ease: 'power2.out',
				});
				if (block.children.length) {
					tl.to(
						block.children,
						{
							autoAlpha: 1,
							duration: 0.4,
							ease: 'power2.out',
						},
						'-=0.3'
					);
				}
			});
		},
	});
}

/**
 * aspiration-mission-card--01 / --03 内の figure を、
 * スクロールで右上から定位置へ寄せつつ、左を軸に右上が上がる向きへ傾いた状態から
 * 弧を描くように表示するアニメーション
 */
function initMissionFigureReveal(): void {
	const figures = document.querySelectorAll<HTMLElement>(
		'.aspiration-mission-card--01 figure, .aspiration-mission-card--03 figure'
	);
	if (!figures.length) {
		return;
	}

	figures.forEach((figure) => {
		// 初期状態: 右上にオフセットし、左を軸に右上が上がる向き(反時計回り)へ傾けて非表示
		gsap.set(figure, {
			transformOrigin: 'left center',
			rotate: -10,
			xPercent: 16,
			yPercent: -10,
			autoAlpha: 0,
		});

		// 定位置へ寄せながら rotate を 0 に戻すと、弧を描いて収まる
		gsap.to(figure, {
			rotate: 0,
			xPercent: 0,
			yPercent: 0,
			autoAlpha: 1,
			duration: 1.1,
			ease: 'power3.out',
			scrollTrigger: {
				trigger: figure,
				start: 'top 85%',
				once: true,
			},
		});
	});
}

/**
 * aspiration-vision: sticky がピン留めされている区間に連動して、
 * 2つの円(01/02)が円弧を描きながら位置を入れ替える。
 * 円自体は回転させず x/y で移動するため、ラベルは常に正立。
 */
function initVisionCircleSwap(): void {
	const content = document.querySelector<HTMLElement>(
		'.aspiration-vision-content'
	);
	const sticky = document.querySelector<HTMLElement>(
		'.aspiration-vision-sticky'
	);
	const endTrigger =
		document.querySelector<HTMLElement>('.aspiration-vision-img') ||
		content;
	const c1 = document.querySelector<HTMLElement>(
		'.aspiration-vision-circle--01'
	);
	const c2 = document.querySelector<HTMLElement>(
		'.aspiration-vision-circle--02'
	);
	if (!content || !sticky || !c1 || !c2) {
		return;
	}

	const mm = gsap.matchMedia();

	mm.add('(min-width: 782px)', () => {
		// SVG楕円の 短径/長径。円弧を真円ではなく楕円に近づけるための比率。
		const RATIO = 111.343 / 166.315;

		// 基準値（リフレッシュ毎に再計測）。
		let v1x = 0; // 中心→circle01（長軸方向＝長径ベクトル）
		let v1y = 0;
		let majA = 0; // 長径(px)
		let ux = 0; // 長軸方向の単位ベクトル
		let uy = 0;
		let minB = 0; // 短径(px)
		let nx = 0; // 短軸(法線)方向の単位ベクトル
		let ny = 0;

		// レイアウト確定後に基準位置を実測（リフレッシュ毎に再計算）。
		const measure = () => {
			gsap.set([c1, c2], { x: 0, y: 0 });
			const r1 = c1.getBoundingClientRect();
			const r2 = c2.getBoundingClientRect();
			const c1cx = r1.left + r1.width / 2;
			const c1cy = r1.top + r1.height / 2;
			const cx = (c1cx + r2.left + r2.width / 2) / 2;
			const cy = (c1cy + r2.top + r2.height / 2) / 2;
			v1x = c1cx - cx;
			v1y = c1cy - cy;
			majA = Math.hypot(v1x, v1y) || 1;
			ux = v1x / majA;
			uy = v1y / majA;
			minB = majA * RATIO;
			// û を +90°回転した短軸方向。時計回りに膨らむ向き。
			nx = -uy;
			ny = ux;
		};

		// 進捗 prog(0→1)で半周。01は θ:0→π、02は θ:π→2π を楕円に沿って時計回りに移動。
		const applyProgress = (prog: number) => {
			const t = prog * Math.PI;
			const p1x = Math.cos(t) * majA * ux + Math.sin(t) * minB * nx;
			const p1y = Math.cos(t) * majA * uy + Math.sin(t) * minB * ny;
			gsap.set(c1, { x: p1x - v1x, y: p1y - v1y });

			const t2 = Math.PI + t;
			const p2x = Math.cos(t2) * majA * ux + Math.sin(t2) * minB * nx;
			const p2y = Math.cos(t2) * majA * uy + Math.sin(t2) * minB * ny;
			// circle02 の基準オフセットは -v1。
			gsap.set(c2, { x: p2x + v1x, y: p2y + v1y });
		};

		measure();

		const state = { p: 0 };
		gsap.to(state, {
			p: 1,
			ease: 'power2.inOut',
			onUpdate: () => applyProgress(state.p),
			scrollTrigger: {
				trigger: sticky,
				start: 'top top',
				endTrigger,
				end: 'bottom bottom',
				scrub: 5,
				onRefresh: measure,
			},
		});

		return () => {
			gsap.set([c1, c2], { clearProps: 'transform' });
		};
	});
}

/**
 * .js-add-spans 内のテキストを <br> ごとに <span> で囲む
 */
function wrapInterviewPostsCopyLines(): void {
	const copyElements =
		document.querySelectorAll<HTMLElement>('.js-add-spans');

	copyElements.forEach((element) => {
		// すでに処理済みの場合はスキップ
		if (element.dataset.linesWrapped) {
			return;
		}

		// innerHTMLを<br>または<br/>または<br />で分割
		const html = element.innerHTML;
		const lines = html.split(/<br\s*\/?>/gi);

		// 各行を<span>で囲んで再構築
		element.innerHTML = lines
			.map((line) => `<span>${line.trim()}</span>`)
			.join('');

		// 処理済みマーク
		element.dataset.linesWrapped = 'true';
	});
}

/**
 * data-card-number-text の数字をカウントアップするアニメーション
 */
function initCountUpAnimation(): void {
	const numberTexts = document.querySelectorAll<HTMLElement>(
		'.data-card-number-text'
	);

	numberTexts.forEach((element) => {
		// テキストノードから数字を抽出（spanなどの子要素は除外）
		const childNodes = Array.from(element.childNodes);
		let targetNumber = 0;
		let textNode: Text | null = null;

		for (const node of childNodes) {
			if (node.nodeType === Node.TEXT_NODE) {
				const match = node.textContent?.match(/[\d.]+/);
				if (match) {
					targetNumber = parseFloat(match[0]);
					textNode = node as Text;
					break;
				}
			}
		}

		if (!textNode || targetNumber === 0) {
			return;
		}

		// 小数点以下の桁数を取得
		const decimalPlaces = (targetNumber.toString().split('.')[1] || '')
			.length;

		// 初期値を0に設定
		const originalText = textNode.textContent || '';
		textNode.textContent = originalText.replace(
			/[\d.]+/,
			decimalPlaces > 0 ? '0.' + '0'.repeat(decimalPlaces) : '0'
		);

		// カウントアップ用のオブジェクト
		const counter = { value: 0 };

		// ScrollTriggerでアニメーション
		gsap.to(counter, {
			value: targetNumber,
			duration: 2,
			ease: 'power2.out',
			scrollTrigger: {
				trigger: element,
				start: 'top 80%',
				once: true,
			},
			onUpdate: () => {
				if (textNode) {
					const formattedValue =
						decimalPlaces > 0
							? counter.value.toFixed(decimalPlaces)
							: Math.round(counter.value).toString();
					textNode.textContent = originalText.replace(
						/[\d.]+/,
						formattedValue
					);
				}
			},
		});
	});
}

/**
 * data-card-numberの擬似要素とdata-card-img内の.js-randomColorの色を
 * theme.jsonのaccentcolorからランダムに選んで変更する
 */
function initRandomAccentColor(): void {
	const randomColor = pickRandomAccentColor();

	// CSSカスタムプロパティを設定（data-card-number::beforeに適用）
	document.documentElement.style.setProperty(
		'--data-card-number-accent',
		randomColor
	);

	// .data-card-img内の.js-randomColorのfillを変更
	const randomColorElements = document.querySelectorAll<SVGElement>(
		'.data-card-img .js-randomColor'
	);
	randomColorElements.forEach((element) => {
		// fillとstrokeが#caef57のものだけ値を変える
		const fill = element.getAttribute('fill');
		const stroke = element.getAttribute('stroke');
		if (fill === '#caef57') {
			element.setAttribute('fill', randomColor);
		}
		if (stroke === '#caef57') {
			element.setAttribute('stroke', randomColor);
		}
	});
}

/**
 * .loop-text内のテキストを複製して、途切れない無限スクロールを実現する
 *
 * .loop-text の子要素 (通常 spanやdivなど) を複製してDOMに追加します。
 * 設計的には、最低2回以上並ぶように複製しますが、要素幅によってはウィンドウ幅の2倍以上になるまで増殖させます。
 */
function initLoopTextInfinityScroll() {
	const loopTexts = document.querySelectorAll<HTMLElement>('.loop-text');
	loopTexts.forEach((loopText) => {
		// すでに複製済みなら何もしない（無限増殖防止）
		if (loopText.dataset.infinityScrollInitialized) {
			return;
		}
		// 複製ターゲットとなる最初の子要素を取得
		const original = loopText.firstElementChild;
		if (!original) {
			return;
		}

		// 子要素全体を格納したラッパーを作り、すべての子を移動
		const wrapper = document.createElement('div');
		wrapper.style.display = 'inline-block';
		wrapper.style.whiteSpace = 'nowrap';
		while (loopText.firstChild) {
			wrapper.appendChild(loopText.firstChild);
		}
		loopText.appendChild(wrapper);

		const minWidth = loopText.offsetWidth || window.innerWidth;
		let currentWidth = wrapper.offsetWidth;
		let copyCount = 1;

		// 画面幅の2倍以上になるまで複製
		while (currentWidth < minWidth * 2) {
			const clone = wrapper.cloneNode(true);
			loopText.appendChild(clone);
			currentWidth += (clone as HTMLElement).offsetWidth;
			copyCount++;
			// 複製が100回を超えたら無限ループ予防でストップ
			if (copyCount > 100) {
				break;
			}
		}

		// 初期化済みマーク
		loopText.dataset.infinityScrollInitialized = 'true';
	});
}

const TOP_LINKS_ACTIVE_CLASS = 'top-links-card--active';

/**
 * top-links: リンク行とカードをインデックスで対応させ、ホバー／フォーカスで切り替え（件数可変）
 */
function initTopLinksHoverCards(): void {
	const mq = window.matchMedia('(min-width: 769px)');
	const disposers: Array<() => void> = [];

	function dispose(): void {
		disposers.forEach((d) => {
			d();
		});
		disposers.length = 0;
	}

	function bindColumn(col: HTMLElement): void {
		const linksRoot = col.querySelector<HTMLElement>('.top-links-links');
		const cardsRoot = col.querySelector<HTMLElement>('.top-links-cards');
		if (!linksRoot || !cardsRoot) {
			return;
		}

		const cards = Array.from(
			cardsRoot.querySelectorAll<HTMLElement>('.top-links-card')
		);
		const links = Array.from(
			linksRoot.querySelectorAll<HTMLElement>('.top-links-link')
		);

		if (!cards.length || !links.length) {
			return;
		}

		function setActiveIndex(linkIndex: number): void {
			const cardIndex = Math.min(linkIndex, cards.length - 1);
			cards.forEach((card, i) => {
				card.classList.toggle(TOP_LINKS_ACTIVE_CLASS, i === cardIndex);
			});
		}

		function onLinkPointerEnter(linkIndex: number): void {
			setActiveIndex(linkIndex);
		}

		function onColPointerLeave(): void {
			setActiveIndex(0);
		}

		function onLinksFocusIn(event: FocusEvent): void {
			const target = event.target;
			if (!(target instanceof HTMLElement)) {
				return;
			}
			const row = target.closest<HTMLElement>('.top-links-link');
			if (!row || !linksRoot.contains(row)) {
				return;
			}
			const idx = links.indexOf(row);
			if (idx >= 0) {
				setActiveIndex(idx);
			}
		}

		function onColFocusOut(event: FocusEvent): void {
			const next = event.relatedTarget;
			if (!(next instanceof Node) || !col.contains(next)) {
				setActiveIndex(0);
			}
		}

		const linkHandlers: Array<{ el: HTMLElement; fn: () => void }> = [];

		links.forEach((link, i) => {
			const fn = (): void => {
				onLinkPointerEnter(i);
			};
			link.addEventListener('pointerenter', fn);
			linkHandlers.push({ el: link, fn });
		});

		linksRoot.addEventListener('focusin', onLinksFocusIn);
		col.addEventListener('pointerleave', onColPointerLeave);
		col.addEventListener('focusout', onColFocusOut);

		setActiveIndex(0);
		col.dataset.topLinksJs = '1';

		disposers.push(() => {
			linkHandlers.forEach(({ el, fn }) => {
				el.removeEventListener('pointerenter', fn);
			});
			linksRoot.removeEventListener('focusin', onLinksFocusIn);
			col.removeEventListener('pointerleave', onColPointerLeave);
			col.removeEventListener('focusout', onColFocusOut);
			cards.forEach((card) => {
				card.classList.remove(TOP_LINKS_ACTIVE_CLASS);
			});
			delete col.dataset.topLinksJs;
		});
	}

	function enable(): void {
		dispose();
		document
			.querySelectorAll<HTMLElement>('.top-links-col')
			.forEach(bindColumn);
	}

	mq.addEventListener('change', () => {
		if (mq.matches) {
			enable();
		} else {
			dispose();
		}
	});

	if (mq.matches) {
		enable();
	}
}

/**
 * .is-style-random-hover の core/button にホバーするたびに --random-hover-color を更新する
 */
function initRandomHoverColor(): void {
	const elements = document.querySelectorAll<HTMLElement>(
		'.wp-block-button.is-style-random-hover'
	);
	elements.forEach((element) => {
		element.addEventListener('mouseenter', () => {
			element.style.setProperty(
				'--random-hover-color',
				pickRandomAccentColor()
			);
		});
	});
}

/**
 * 追従ヘッダーが .has-dark-bg の上にあるときに .l-header へ is-dark クラスを付与する
 */
function initHeaderDarkBg(): void {
	const header = document.querySelector<HTMLElement>('.l-header');
	if (!header) {
		return;
	}

	const darkSections = document.querySelectorAll<HTMLElement>('.has-dark-bg');
	if (!darkSections.length) {
		return;
	}

	const triggers: ScrollTrigger[] = [];

	const updateHeaderClass = () => {
		const anyActive = triggers.some((t) => t.isActive);
		header.classList.toggle('is-dark', anyActive);
		header.classList.toggle('is-light', !anyActive);
	};

	darkSections.forEach((section) => {
		const trigger = ScrollTrigger.create({
			trigger: section,
			start: () => 'top top+=' + header.offsetHeight,
			end: 'bottom top',
			onToggle: updateHeaderClass,
		});
		triggers.push(trigger);
	});

	updateHeaderClass();
}

/**
 * DOMContentLoaded時に初期化
 */
document.addEventListener('DOMContentLoaded', () => {
	initLenis();
	initHeroAnimation();
	initTopJobAnimation();
	initMissionFigureReveal();
	initVisionCircleSwap();
	initBlockScaleInAnimation('.faq-block');
	initLoopTextInfinityScroll();
	wrapInterviewPostsCopyLines();
	initRandomAccentColor();
	initCountUpAnimation();
	initTopLinksHoverCards();
	initHeaderDarkBg();
	initRandomHoverColor();
});
